import { Box } from "@mui/material";
import { Redirect } from "react-router-dom";
import TabsComponent from "../../components/Tabs/Tabs";
import BillingHistory from "../BillingHistory/BillingHistory";
import ChargesHistory from "../ChargesHistory/ChargesHistory";
import CommissionsHistory from "../CommissionsHistory/CommissionsHistory";
import PaymentConfigurationsList from "../PaymentConfigurations/PaymentConfigurationsList";
import PaymentTermsGate from "../../components/payments/PaymentTermsGate/PaymentTermsGate";
import CompanySelector from "../../components/payments/CompanySelector/CompanySelector";
import Loading from "../../components/Loading/Loading";
import Resume from "./Resume";
import { SelectedPaymentConfigurationProvider } from "../../state/selectedPaymentConfiguration";
import usePaymentConfigurations from "../../hooks/usePaymentConfigurations";
import {
  PAYMENTS_TABS,
  PAYMENTS_WELCOME_PATH,
  paymentsTabPath
} from "../../utils/paymentsRoutes";
import { useTranslation } from "react-i18next";

/**
 * The tabs of the payments area.
 *
 * A professional with no account at all never sees them: there is no summary to
 * show, no history to list and nothing to configure, so they are sent to the
 * welcome screen instead. One account is enough to bring the tabs back, even an
 * account still waiting on its documents or on approval — from that point on
 * the welcome screen is behind them for good.
 */
function PaymentsAccountTabs({ type, onTabChange }: Props) {
  const { t } = useTranslation();
  const { configurations, loading } = usePaymentConfigurations();

  // Only while the answer is still unknown: deciding on an empty list that has
  // not loaded yet would send everyone through the welcome screen on their way
  // in, however many accounts they have.
  if (loading && configurations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" mt={10}>
        <Loading />
      </Box>
    );
  }

  if (configurations.length === 0) {
    return <Redirect to={PAYMENTS_WELCOME_PATH} />;
  }

  return (
    <TabsComponent
      value={PAYMENTS_TABS.indexOf(type as any)}
      // Every tab below shows the data of one company, so the selector lives
      // with the tabs instead of being repeated inside each of them.
      toolbar={<CompanySelector />}
      tabs={[
        {
          label: t("paymentsAccount.summary"),
          contentBody: <Resume />
        },
        {
          label: t("paymentsAccount.paymentsHistory"),
          contentBody: <ChargesHistory />
        },
        {
          label: t("paymentsAccount.billingHistory"),
          contentBody: <BillingHistory />
        },
        {
          label: t("paymentsAccount.commissionsHistory"),
          contentBody: <CommissionsHistory />
        },
        {
          label: t("paymentsAccount.configurations"),
          contentBody: <PaymentConfigurationsList />
        }
      ]}
      handleChange={index => onTabChange(paymentsTabPath(PAYMENTS_TABS[index]))}
    />
  );
}

interface Props {
  type: string;
  onTabChange: (path: string) => void;
}

export default function PaymentsAccount(props: any) {
  return (
    <PaymentTermsGate>
      <SelectedPaymentConfigurationProvider>
        <PaymentsAccountTabs
          type={props.match.params.type}
          onTabChange={path => props.history.push(path)}
        />
      </SelectedPaymentConfigurationProvider>
    </PaymentTermsGate>
  );
}
